import {
  customerQuotationsSchema,
  quotationDownloadSchema,
  quotationPageSchema,
  quotationSchema,
  type AcceptQuotationInput,
  type CreateQuotationInput,
  type CustomerQuotations,
  type Quotation,
  type QuotationDownload,
  type QuotationPage,
  type QuotationStatus,
  type RejectQuotationInput,
  type ReviseQuotationInput,
  type SendQuotationInput,
  type SendQuotationOnWhatsappInput,
  type UpdateQuotationInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every quotation call the app makes.
 *
 * Note which verbs exist and which do not. There is a `del` and it takes a DRAFT — the API
 * refuses to remove anything that has been sent, because a customer may be holding it. What
 * replaces a sent quotation is `reviseQuotation`, and what ends one is accept or reject.
 *
 * `sendQuotation` and `sendQuotationOnWhatsapp` are two different acts and the names are worth
 * keeping apart: the first draws the number and freezes the figures, and is the moment a price
 * becomes a price. The second puts an already-sent document on somebody's phone, and may be done
 * again — the relay is keyed per document per recipient, so a second press is not a second copy.
 */

const QUOTATIONS = '/quotations';

export interface QuotationSearchParams {
  q?: string;
  /** Stored statuses. The computed states — lapsed, replaced — cannot be queried; see the API. */
  status?: readonly QuotationStatus[];
  page?: number;
  pageSize?: number;
}

/** Everything the shop has quoted, newest first. What the quotations screen reads. */
export async function searchQuotations(
  client: ApiClient,
  params: QuotationSearchParams = {},
): Promise<QuotationPage> {
  return await client.get(QUOTATIONS, {
    schema: quotationPageSchema,
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.status?.length ? { status: [...params.status] } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}

/** Every quotation given to one customer, newest first. What the card on the customer reads. */
export async function getCustomerQuotations(
  client: ApiClient,
  customerId: string,
): Promise<CustomerQuotations> {
  return await client.get(`${QUOTATIONS}/customer/${customerId}`, {
    schema: customerQuotationsSchema,
  });
}

export async function getQuotation(client: ApiClient, quotationId: string): Promise<Quotation> {
  return await client.get(`${QUOTATIONS}/${quotationId}`, { schema: quotationSchema });
}

export async function createQuotation(
  client: ApiClient,
  input: CreateQuotationInput,
): Promise<Quotation> {
  return await client.post(QUOTATIONS, input, { schema: quotationSchema });
}

/** A draft only. Sending freezes the document; changing it afterwards means a revision. */
export async function updateQuotation(
  client: ApiClient,
  quotationId: string,
  patch: UpdateQuotationInput,
): Promise<Quotation> {
  return await client.patch(`${QUOTATIONS}/${quotationId}`, patch, { schema: quotationSchema });
}

export async function discardQuotation(client: ApiClient, quotationId: string): Promise<void> {
  await client.del(`${QUOTATIONS}/${quotationId}`);
}

/** Draws the number, freezes the figures, supersedes the version this one replaces. */
export async function sendQuotation(
  client: ApiClient,
  quotationId: string,
  input: SendQuotationInput = {},
): Promise<Quotation> {
  return await client.post(`${QUOTATIONS}/${quotationId}/send`, input, { schema: quotationSchema });
}

/** Opens version n+1 as a draft. The one being revised is untouched until the new one is sent. */
export async function reviseQuotation(
  client: ApiClient,
  quotationId: string,
  input: ReviseQuotationInput = {},
): Promise<Quotation> {
  return await client.post(`${QUOTATIONS}/${quotationId}/revise`, input, {
    schema: quotationSchema,
  });
}

export async function acceptQuotation(
  client: ApiClient,
  quotationId: string,
  input: AcceptQuotationInput = {},
): Promise<Quotation> {
  return await client.post(`${QUOTATIONS}/${quotationId}/accept`, input, {
    schema: quotationSchema,
  });
}

export async function rejectQuotation(
  client: ApiClient,
  quotationId: string,
  input: RejectQuotationInput = {},
): Promise<Quotation> {
  return await client.post(`${QUOTATIONS}/${quotationId}/reject`, input, {
    schema: quotationSchema,
  });
}

/**
 * A short-lived link to the rendered PDF. A4 only — a quotation has no thermal variant, because
 * it is a document a customer keeps and compares, not a slip handed over a counter.
 */
export async function getQuotationDownload(
  client: ApiClient,
  quotationId: string,
  options: { refresh?: boolean } = {},
): Promise<QuotationDownload> {
  return await client.get(`${QUOTATIONS}/${quotationId}/download`, {
    schema: quotationDownloadSchema,
    ...(options.refresh ? { query: { refresh: 'true' } } : {}),
  });
}

export async function sendQuotationOnWhatsapp(
  client: ApiClient,
  quotationId: string,
  input: SendQuotationOnWhatsappInput = {},
): Promise<Quotation> {
  return await client.post(`${QUOTATIONS}/${quotationId}/whatsapp`, input, {
    schema: quotationSchema,
  });
}
